import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { Modal } from '@/shared/components/Modal';
import { Button } from '@/shared/components/Button';
import { LOOKUP_LABEL_SEPARATOR } from '@/shared/format/lookupLabel';
import { WINDOW_TRANSLATIONS_QUERY_KEY } from './useWindowTranslations';

/**
 * slice-4427 (ADR-0075, John 24 Oct Q2): PB's File > Change Language
 * (`m_phfc_main.srm:1988-2002`, caption "Change &Language", window `w_changelang` →
 * `w_phfc_changelang`, title "Change Current Language" srw:29). PH10's File-menu items live in
 * the profile menu (ADR-0039/0061), so it sits there beside Owners/Warehouses.
 *
 * Layout = `dw_phfc_change_language.srd`: two columns headed "Base" and "Work Stattion" (PB's
 * spelling), a read-only "Current Language:" row and a "Select New Language:" dropdown row; both
 * dropdowns start with "(Default)" (srw:45/55). OK sends both choices; the server answers PB's
 * "Please choose at least a different Language to change." (srw:93-96) and rejects a language
 * that is not in the client database (422). On success the session's languages change — PB keeps
 * them only in session globals and never writes `usrs` (srw:98-104) — every open screen re-reads
 * its labels (`f_phfc_change_language.srf:25-38`) and PB's "Your Language was successfully
 * changed." (srf:51) is shown. The Work Station language is the one window translations use
 * (`gs_app_language_id`, `uo_phfc_crt_translate_util.sru:417-421`).
 */
export type LanguageOption = { id: string; description: string | null };

export type ChangeLanguageState = {
  languageOld: string;
  languageWsOld: string;
  languages: LanguageOption[];
  wsLanguages: LanguageOption[];
};

const PATH = '/api/platform/window-translations/change-language';
const CHANGE_LANGUAGE_QUERY_KEY = ['platform', 'change-language'] as const;

export function ChangeLanguageDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: CHANGE_LANGUAGE_QUERY_KEY,
    queryFn: () => api<ChangeLanguageState>(PATH),
    staleTime: 0,
  });
  const [base, setBase] = useState<string | null>(null);
  const [ws, setWs] = useState<string | null>(null);
  const data = query.data;
  useEffect(() => {
    if (!data) return;
    setBase((prev) => prev ?? data.languageOld);
    setWs((prev) => prev ?? data.languageWsOld);
  }, [data]);

  const change = useMutation<ChangeLanguageState, Error, { language: string; wsLanguage: string }>({
    mutationFn: (body) => api<ChangeLanguageState>(PATH, { method: 'PUT', json: body }),
    onSuccess: (next) => {
      qc.setQueryData(CHANGE_LANGUAGE_QUERY_KEY, next);
      void qc.invalidateQueries({ queryKey: WINDOW_TRANSLATIONS_QUERY_KEY });
      onClose();
      window.alert('Your Language was successfully changed.');
    },
  });

  const ready = data !== undefined && base !== null && ws !== null;

  function handleOk() {
    if (base === null || ws === null) return;
    change.mutate({ language: base, wsLanguage: ws });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Change Current Language"
      size="md"
      footer={
        <>
          <Button onClick={handleOk} disabled={!ready || change.isPending}>
            OK
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      {query.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {query.isError && (
        <p role="alert" className="text-sm text-red-700">
          {query.error instanceof Error ? query.error.message : 'Could not load the languages.'}
        </p>
      )}
      {data && (
        <div className="grid grid-cols-[auto_1fr_1fr] items-center gap-x-3 gap-y-2 text-sm">
          <span />
          <span className="text-slate-600">Base</span>
          <span className="text-slate-600">Work Stattion</span>
          <span>Current Language:</span>
          <span className="rounded border bg-slate-50 px-2 py-1 font-mono" data-testid="language-old">
            {data.languageOld}
          </span>
          <span className="rounded border bg-slate-50 px-2 py-1 font-mono" data-testid="language-ws-old">
            {data.languageWsOld}
          </span>
          <label htmlFor="change-language-new">Select New Language:</label>
          <LanguageSelect
            id="change-language-new"
            ariaLabel="Base language"
            value={base ?? ''}
            options={data.languages}
            onChange={setBase}
          />
          <LanguageSelect
            id="change-language-ws-new"
            ariaLabel="Work Station language"
            value={ws ?? ''}
            options={data.wsLanguages}
            onChange={setWs}
          />
        </div>
      )}
      {change.isError && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {change.error.message}
        </p>
      )}
    </Modal>
  );
}

function LanguageSelect({
  id,
  ariaLabel,
  value,
  options,
  onChange,
}: {
  id: string;
  ariaLabel: string;
  value: string;
  options: LanguageOption[];
  onChange: (v: string) => void;
}) {
  return (
    <select
      id={id}
      aria-label={ariaLabel}
      className="rounded border px-2 py-1 font-mono"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.description ? `${o.id}${LOOKUP_LABEL_SEPARATOR}${o.description}` : o.id}
        </option>
      ))}
    </select>
  );
}
